import type { ComponentType } from "react";

/**
 * Top-level route table: which lazy chunk owns which path. `App.tsx` picks the longest matching
 * prefix and loads only that chunk, so the game (and Three.js) downloads only on `/play`. Adding a
 * page is one row. `auth` is what the page will require once accounts land; it is advisory here
 * (the server enforces access, e.g. the game socket), never a client-side gate.
 *
 * The server serves index.html for every non-API GET (apps/server/src/static.ts), and the service
 * worker's navigateFallback does the same offline, so every path here survives a reload.
 */
type RouteAuth = "none" | "account" | "admin";
type RouteId = "site" | "play" | "admin" | "account";

export interface Route {
  id: RouteId;
  /** `/pricing` matches `/pricing` and `/pricing/...`, not `/pricingx`. */
  prefix: string;
  /** Match the path exactly (the landing page owns `/` but not everything under it). */
  exact?: boolean;
  auth: RouteAuth;
  chunk: () => Promise<{ default: ComponentType }>;
}

const site = () => import("@/site/SiteApp");
const account = () => import("@/account/AccountApp");

export const ROUTES: readonly Route[] = [
  { id: "site", prefix: "/", exact: true, auth: "none", chunk: site },
  { id: "site", prefix: "/pricing", auth: "none", chunk: site },
  { id: "site", prefix: "/faq", auth: "none", chunk: site },
  { id: "site", prefix: "/legal", auth: "none", chunk: site },
  { id: "site", prefix: "/contact", auth: "none", chunk: site },
  { id: "play", prefix: "/play", auth: "account", chunk: () => import("@/game/PlayRoute") },
  { id: "admin", prefix: "/admin", auth: "admin", chunk: () => import("@/admin/AdminApp") },
  { id: "account", prefix: "/signup", auth: "none", chunk: account },
  { id: "account", prefix: "/invite", auth: "none", chunk: account },
  { id: "account", prefix: "/login", auth: "none", chunk: account },
  { id: "account", prefix: "/reset", auth: "none", chunk: account },
  // the password_changed email links here
  { id: "account", prefix: "/forgot", auth: "none", chunk: account },
  { id: "account", prefix: "/verify", auth: "none", chunk: account },
  { id: "account", prefix: "/waitlist", auth: "none", chunk: account },
  { id: "account", prefix: "/account", auth: "account", chunk: account },
];

/** Unknown paths render the site, which shows its 404 page. */
export const NOT_FOUND_ROUTE: Route = {
  id: "site",
  prefix: "",
  auth: "none",
  chunk: site,
};

function matches(route: Route, path: string): boolean {
  if (route.exact) return path === route.prefix;
  return path === route.prefix || path.startsWith(`${route.prefix}/`);
}

export function resolveRoute(pathname: string, routes: readonly Route[] = ROUTES): Route {
  // a trailing slash ("/pricing/") is the same page
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") || "/" : pathname;
  let best: Route | null = null;
  for (const r of routes) {
    if (matches(r, path) && (!best || r.prefix.length > best.prefix.length)) best = r;
  }
  return best ?? NOT_FOUND_ROUTE;
}

/**
 * Query flags the game reads (`?debug=1` debug menu, `?touch` touch mode, `?perf` frame stats).
 * Before the site existed the game lived at `/`, so bookmarks and docs say `/?debug=1`.
 */
const LEGACY_GAME_PARAMS = ["debug", "touch", "perf"] as const;

/** Where an old game deep link (`/?debug=1`) now lives (`/play?debug=1`), or null. */
export function legacyGameRedirect(pathname: string, search: string, hash = ""): string | null {
  if (pathname !== "/" || !search) return null;
  const params = new URLSearchParams(search);
  if (!LEGACY_GAME_PARAMS.some((p) => params.has(p))) return null;
  return `/play${search}${hash}`;
}
